// After Microsoft sign-in, the session is MFA-pending until a second factor succeeds: a passkey
// ceremony (apps/pipeline_api/src/mfa.rs) or a Duo approval (src/duo.rs). This page is all an
// MFA-pending session can reach.
import { useQueryClient } from "@tanstack/react-query";
import { KeyRound, Mail, Smartphone } from "lucide-react";
import { useEffect, useState } from "react";
import { Button, Spinner } from "../components/ui";
import { ApiError } from "../lib/api";
import { duoEnrollError, duoErrorMessage, duoStartError, requestDuoSetup, startDuo, takeDuoError, type DuoEnrollment } from "../lib/duo";
import { confirmPasskey, passkeyError, passkeysSupported, registerPasskey, useMfaState, type MfaState } from "../lib/passkeys";
import { qk } from "../lib/queries";
import { Frame, logout, SignInPage } from "./AuthPages";

/** What the page offers for this state. */
export function mfaStep(s: Pick<MfaState, "has_passkey" | "enroll_basis">): "confirm" | "setup" | "ask_admin" {
  if (s.has_passkey) return "confirm";
  return s.enroll_basis ? "setup" : "ask_admin";
}

function until(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export function MfaPage() {
  const qc = useQueryClient();
  const state = useMfaState();
  const [busy, setBusy] = useState(false);
  const [enrollingDuo, setEnrollingDuo] = useState(false);
  const [duoSetup, setDuoSetup] = useState<DuoEnrollment | null>(null);
  // Duo sends the browser back here with `?duo_error=<code>` when it didn't approve.
  const [duoReturn] = useState(() => new URLSearchParams(window.location.search).get("duo_error"));
  const [err, setErr] = useState<string | null>(duoReturn ? duoErrorMessage(duoReturn) : null);
  useEffect(() => {
    if (duoReturn) takeDuoError();
  }, [duoReturn]);
  const [label, setLabel] = useState("");
  const supported = passkeysSupported();

  const done = async () => {
    await qc.invalidateQueries({ queryKey: qk.me });
  };
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      await done();
    } catch (e) {
      setErr(passkeyError(e));
      // The allowance may have expired, or an admin may have changed something: refresh the step.
      if (e instanceof ApiError) void state.refetch();
    } finally {
      setBusy(false);
    }
  };

  const runDuo = async () => {
    setBusy(true);
    setErr(null);
    try {
      // Navigates to Duo's prompt; Duo brings the browser back to the callback.
      await startDuo();
    } catch (e) {
      setErr(duoStartError(e));
      setBusy(false);
    }
  };

  const runDuoSetup = async () => {
    setEnrollingDuo(true);
    setErr(null);
    try {
      const res = await requestDuoSetup();
      setDuoSetup(res);
    } catch (e) {
      setErr(duoEnrollError(e));
    } finally {
      setEnrollingDuo(false);
    }
  };

  if (state.isLoading) {
    return (
      <Frame>
        <div className="flex items-center gap-2 text-ink-2">
          <Spinner /> Loading…
        </div>
      </Frame>
    );
  }
  if (state.error || !state.data) {
    const e = state.error;
    if (e instanceof ApiError && e.status === 401) return <SignInPage />;
    return (
      <Frame>
        <h1 className="font-display text-[22px] font-semibold text-ink">Couldn't check your sign-in</h1>
        <p className="mt-3 text-ink-2">{(e as Error | null)?.message ?? "Try again."}</p>
        <div className="mt-6 flex gap-2">
          <Button variant="primary" onClick={() => void state.refetch()}>
            Retry
          </Button>
          <Button onClick={() => void logout(qc)}>Sign out</Button>
        </div>
      </Frame>
    );
  }

  const s = state.data;
  const step = mfaStep(s);
  const duo = s.duo_available;
  return (
    <Frame>
      <div className="eyebrow text-navy-700 dark:text-navy-300">Two-step sign-in</div>
      {duo && step !== "setup" ? (
        <>
          <h1 className="font-display mt-1 text-[22px] font-semibold text-ink">Confirm your sign-in</h1>
          <p className="mt-3 text-ink-2">
            You're signed in with Microsoft as <span className="font-medium text-ink">{s.user.email}</span>. Approve the Duo request on your phone
            {step === "confirm" ? ", or use your LoanShip passkey," : ""} to finish signing in.
          </p>
        </>
      ) : step === "confirm" ? (
        <>
          <h1 className="font-display mt-1 text-[22px] font-semibold text-ink">Confirm with your passkey</h1>
          <p className="mt-3 text-ink-2">
            You're signed in with Microsoft as <span className="font-medium text-ink">{s.user.email}</span>. Use your LoanShip passkey (fingerprint, face or device PIN) to
            finish signing in.
          </p>
        </>
      ) : step === "setup" ? (
        <>
          <h1 className="font-display mt-1 text-[22px] font-semibold text-ink">Set up your passkey</h1>
          <p className="mt-3 text-ink-2">
            LoanShip asks for a passkey on every sign-in, as well as your Microsoft account. Create one now on this device — your fingerprint, face or device PIN unlocks
            it, and it never leaves your device.
          </p>
          {s.enroll_basis === "allowance" && until(s.enrollment_expires_at) ? (
            <p className="mt-2 text-[13px] text-ink-3">Set-up is open until {until(s.enrollment_expires_at)}.</p>
          ) : null}
          <label className="mt-4 block text-[12px] font-medium text-ink-2">
            Name this passkey (optional)
            <input
              value={label}
              maxLength={60}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Work laptop"
              className="focus-ring mt-1 block h-9 w-full rounded-sm border border-input bg-surface px-2.5 text-[14px] text-ink"
            />
          </label>
        </>
      ) : (
        <>
          <h1 className="font-display mt-1 text-[22px] font-semibold text-ink">Ask an administrator to enable 2FA enrollment</h1>
          <p className="mt-3 text-ink-2">
            You're signed in with Microsoft as <span className="font-medium text-ink">{s.user.email}</span>, but LoanShip also needs a passkey, and setting one up has to be
            enabled for your account first. Ask a LoanShip administrator to <span className="font-medium text-ink">enable 2FA enrollment</span> for you, then choose Check
            again.
          </p>
        </>
      )}

      {!supported && step !== "ask_admin" && !duo ? (
        <div role="alert" className="mt-4 rounded-md bg-warning-bg px-3 py-2 text-[13px] text-warning">
          This browser doesn't support passkeys. Open LoanShip in a current version of Edge, Chrome, Safari or Firefox.
        </div>
      ) : null}
      {err ? (
        <div role="alert" className="mt-4 rounded-md bg-danger-bg px-3 py-2 text-[13px] text-danger">
          {err}
        </div>
      ) : null}
      {duoSetup ? (
        <div role="status" data-testid="duo-setup-confirmation" className="mt-4 rounded-md border border-emerald-200 bg-emerald-50/90 px-3.5 py-3 text-[13px] text-emerald-950 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100">
          {duoSetup.delivery === "email" ? (
            <p>
              We sent a Duo setup email to <span className="font-semibold">{duoSetup.email}</span>. Open the email on your phone to activate Duo Mobile, then choose{" "}
              <span className="font-semibold">Approve on my phone (Duo)</span> below.
            </p>
          ) : (
            <div className="space-y-2.5">
              <p>
                Scan this QR code in the <span className="font-semibold">Duo Mobile</span> app on your phone (or open the activation link directly on your phone) to finish setting up Duo for{" "}
                <span className="font-semibold">{duoSetup.email}</span>:
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <img
                  src={duoSetup.activation_barcode}
                  alt="Duo Mobile activation QR code"
                  className="h-36 w-36 rounded border border-emerald-300 bg-white p-1.5"
                />
                <div className="flex flex-col gap-1.5 text-[12px]">
                  <a
                    href={duoSetup.activation_code}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium underline"
                  >
                    Open Duo activation link on this device
                  </a>
                  <a
                    href={`mailto:${encodeURIComponent(duoSetup.email)}?subject=${encodeURIComponent("Set up Duo Mobile for LoanShip")}&body=${encodeURIComponent(`Hi,\n\nOpen this link on your phone to activate Duo Mobile for LoanShip (${duoSetup.email}):\n${duoSetup.activation_code}\n`)}`}
                    className="font-medium underline"
                  >
                    Email this setup link to {duoSetup.email}
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : null}

      <div className="mt-6 flex flex-wrap gap-2">
        {duo ? (
          <Button variant="primary" disabled={busy || enrollingDuo} onClick={() => void runDuo()}>
            {busy ? <Spinner label="Opening Duo" /> : <Smartphone size={15} />} Approve on my phone (Duo)
          </Button>
        ) : null}
        {duo ? (
          <Button
            variant="secondary"
            data-testid="request-duo-setup-btn"
            disabled={busy || enrollingDuo}
            onClick={() => void runDuoSetup()}
          >
            {enrollingDuo ? <Spinner label="Sending Duo setup" /> : <Mail size={15} />} Send Duo setup email
          </Button>
        ) : null}
        {step === "confirm" ? (
          <Button variant={duo ? "secondary" : "primary"} disabled={busy || enrollingDuo || !supported} onClick={() => void run(confirmPasskey)}>
            {busy && !duo ? <Spinner label="Waiting for your passkey" /> : <KeyRound size={15} />} Use a passkey
          </Button>
        ) : step === "setup" ? (
          <Button variant={duo ? "secondary" : "primary"} disabled={busy || enrollingDuo || !supported} onClick={() => void run(() => registerPasskey(label))}>
            {busy && !duo ? <Spinner label="Waiting for your passkey" /> : <KeyRound size={15} />} Create passkey
          </Button>
        ) : duo ? null : (
          <Button variant="primary" disabled={busy} onClick={() => void state.refetch()}>
            Check again
          </Button>
        )}
        <Button onClick={() => void logout(qc)}>Sign out</Button>
      </div>
      {step === "confirm" ? (
        <p className="mt-4 text-[12px] text-ink-3">Lost the device with your passkey? Ask a LoanShip administrator to reset your 2FA.</p>
      ) : null}
      {duo ? (
        <p className="mt-2 text-[12px] text-ink-3">
          Duo asks for <span className="font-medium">{s.duo_username}</span>. First time signing in with Duo? Choose{" "}
          <button
            type="button"
            disabled={busy || enrollingDuo}
            onClick={() => void runDuoSetup()}
            className="font-medium text-ink underline hover:text-navy-700 disabled:opacity-50 dark:hover:text-navy-300"
          >
            Send Duo setup email
          </button>{" "}
          to activate Duo Mobile on your phone.
        </p>
      ) : null}
    </Frame>
  );
}
